import {
  SecondaryContainer as CommonContainer,
  NewPagination,
  NoContent,
  Loading,
} from "components";
import styled from "styled-components";
import { responsive } from "utils/theme";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import Image from "components/Image";
import { ApiService } from "services/serverInClient";
import { Formatter } from "utils/formatter";
import { toast } from "react-toastify";
// import Toast from "components/Toast/Toast";
import { careerListNormalizer } from "utils/normalizer";
import type { GetContentParams } from "types";
import { useLocale } from "hooks";
import translation from "./translation";
import { useForm } from "react-hook-form";

const SIZE = 6;

const NewsEventCard = ({ data, className, size = "small", single = false }) => {
  return (
    <Link href={data.link} prefetch={false}>
      <a
        className={clsx(
          "tw-flex tw-w-full tw-flex-col tw-group tw-m-0",
          className,
          {
            "sm:tw-w-1/2 tw-mx-auto": single,
            "lg:tw-w-2/3": size === "large",
            "lg:tw-w-[calc(100%/3)]": size !== "large",
          }
        )}
      >
        <div
          className={clsx("tw-w-full", {
            "md:tw-aspect-[6/2]": size === "large",
          })}
        >
          <div
            className={clsx(
              "tw-relative tw-rounded-lg tw-h-full tw-overflow-hidden",
              {
                "tw-aspect-[3/2]": size !== "large",
                "tw-aspect-[6/2]": size === "large",
              }
            )}
          >
            <Image
              width={size === "small" ? 300 : 600}
              height={200}
              src={data?.image}
              alt={data?.alt}
              className={clsx(
                "tw-transition-all tw-rounded-lg xxxl:tw-rounded-[10.67px] tw-w-full tw-h-full tw-object-cover"
              )}
            />
            <div className="tw-rounded-lg tw-text-lg xxxl:tw-rounded-[10.67px] tw-font-medium tw-absolute tw-top-0 tw-w-full tw-h-full tw-flex tw-bg-gradient-to-b tw-items-end tw-pr-5 tw-pb-4 gradiant"></div>
          </div>
        </div>
        <span
          className={clsx(
            "tw-text-[#344456] tw-text-base xxxl:tw-text-[21.33px] tw-font-bold tw-mt-4 group-hover:tw-text-[#007FAC] tw-transition-all tw-min-h-[50px] xxxl:tw-mt-5 xxxl:tw-leading-[36.17px] title"
          )}
        >
          {data?.title}
        </span>
      </a>
    </Link>
  );
};

const StyledNewsEventCard = styled(NewsEventCard)`
  .title {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .gradiant {
    background: linear-gradient(
      180deg,
      rgba(52, 68, 86, 0) 66.67%,
      rgba(52, 68, 86, 0.7) 100%
    );
  }
  img {
    transition: transform 0.3s;
    @media (max-width: ${responsive.md}px) {
      transform: scaleY(1.06);
    }
  }

  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`;
function ResearchCenterListComponent({
  data = null,
  tagTree = null,
  pagination = true,
}) {
  const [listData, setListData] = useState<Array<any>>(
    () => data?.researchCenters || []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isRTL, locale } = useLocale();
  const { register, watch } = useForm();

  const fetchData = async (params: GetContentParams) => {
    try {
      setIsLoading(true);
      const apiService = new ApiService("fa", true);
      const response = await apiService.getContent(params, {
        formatter: false,
      });
      setTotalPagesCount(Math.ceil((response?.count || 0) / SIZE));
      const unsortedList = Formatter.formatData({ data: response });
      setListData(careerListNormalizer(unsortedList) || []);
    } catch {
      toast.error("خطایی رخ داد");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tagTree) {
      let params: GetContentParams = {
        tagTrees: [tagTree],
      };
      if (pagination) {
        params = {
          ...params,
          size: SIZE,
          offset: 0,
        };
      }
      fetchData(params);
    }
  }, [tagTree]);

  const handleSearch = () => {
    const q = watch("search") || "";
    let params: GetContentParams = {
      tagTrees: [tagTree as unknown as string],
    };
    if (pagination) {
      params = {
        ...params,
        size: SIZE,
        offset: 0,
      };
    }
    if (q) {
      params = {
        ...params,
        fieldCode: ["jobTitle"],
        fieldValue: [q],
      };
    }
    fetchData(params);
  };

  const researchCenters = useMemo(() => {
    const result = [];
    let clone = JSON.parse(JSON.stringify(listData));
    if (Array.isArray(clone)) {
      (function chunk() {
        result?.push?.(clone?.splice?.(0, 3));
        if (clone?.length > 0) return chunk();
      })();

      return result;
    }
    return [];
  }, [listData]);

  const researchCentersMobile = useMemo(() => {
    const result = [];
    let clone = JSON.parse(JSON.stringify(listData));
    if (Array.isArray(clone)) {
      (function chunk() {
        result.push(clone?.splice?.(0, 2));
        if (clone?.length > 0) return chunk();
      })();

      return result;
    }
    return [];
  }, [listData]);

  const handlePageClick = async (d) => {
    const selected = d?.selected;
    setCurrentPage(selected + 1);
    const q = watch("search");
    let params: GetContentParams = {
      tagTrees: [tagTree],
    };
    if (pagination) {
      params = {
        ...params,
        size: SIZE,
        offset: selected * SIZE,
      };
    }
    if (q) {
      params = {
        ...params,
        fieldCode: ["jobTitle"],
        fieldValue: [q],
      };
    }
    fetchData(params);
  };

  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center">
        <Loading />
      </div>
    );
  }
  return (
    <>
      {/* <Toast /> */}
      <CommonContainer>
        <>
          <div className="tw-flex">
            <div className="tw-relative tw-w-full md:tw-max-w-[310px] xxxl:tw-max-w-[422px]">
              <input
                type="search"
                className={clsx(
                  "tw-bg-[#FFFFFF] tw-text-sm tw-text-[#344456]/50 placeholder:tw-text-[#344456]/20 tw-border tw-border-[#344456]/20 focus-visible:!tw-border focus-visible:!tw-border-[#344456]/20 focus-visible:!tw-border-solid tw-font-medium tw-rounded-lg tw-py-2.5 xxxl:tw-py-4 tw-outline-none tw-min-w-[300px] xxxl:tw-min-w-[412px] tw-w-full xxxl:tw-text-[17.33px] tw-h-[42px] xxxl:tw-h-[54px]",
                  {
                    "tw-text-left tw-pl-4 ltr": !isRTL,
                    "tw-pr-4": isRTL,
                  }
                )}
                placeholder={`${translation?.[locale]?.SEARCH}...`}
                {...register("search")}
              />
              <button
                onClick={() => handleSearch()}
                className={clsx(
                  "tw-absolute tw-top-1 tw-border-0 tw-cursor-pointer tw-bg-[#004883] hover:tw-bg-[#004883]/80 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-p-2 xxxl:tw-p-3.5",
                  { "tw-left-0.5": isRTL, "tw-right-0.5": !isRTL }
                )}
              >
                <svg
                  width="17"
                  height="18"
                  viewBox="0 0 17 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.9225 15.9108L13.8542 12.8425C14.8817 11.5583 15.5 9.93413 15.5 8.16663C15.5 4.03079 12.135 0.666626 8 0.666626C3.865 0.666626 0.5 4.03079 0.5 8.16663C0.5 12.3025 3.865 15.6666 8 15.6666C9.76833 15.6666 11.3925 15.0483 12.6758 14.0216L15.7442 17.0891L16.9225 15.9108ZM8 14C4.78333 14 2.16667 11.3833 2.16667 8.16663C2.16667 4.95079 4.78333 2.33329 8 2.33329C11.2167 2.33329 13.8333 4.95079 13.8333 8.16663C13.8333 11.3833 11.2167 14 8 14Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="tw-hidden lg:tw-block tw-mt-4">
            {researchCenters?.length > 0 ? (
              researchCenters?.map?.((parent) => (
                <div
                  className={clsx(
                    "tw-mb-[30px] last:tw-mb-0 tw-gap-[30px] tw-flex-wrap lg:tw-flex-nowrap",
                    {
                      "md:tw-inline-flex lg:tw-flex tw-justify-center": true,
                      "tw-mx-auto md:tw-px-[30px]": parent?.length === 2,
                    }
                  )}
                >
                  {parent?.length > 0 ? (
                    parent?.map?.((v) => (
                      <StyledNewsEventCard
                        data={v}
                        key={v?.id}
                        size={parent?.length === 1 ? "large" : "small"}
                      />
                    ))
                  ) : (
                    <NoContent />
                  )}
                </div>
              ))
            ) : (
              <NoContent />
            )}
          </div>
          <div className="tw-block lg:tw-hidden tw-mt-4">
            {researchCentersMobile?.length > 0 ? (
              researchCentersMobile?.map?.((parent) => (
                <div
                  className={clsx("tw-mb-[30px] last:tw-mb-0 tw-gap-[30px]", {
                    "tw-flex tw-mx-auto lg:tw-px-[30px]": parent?.length < 2,
                    "tw-grid tw-grid-cols-1 sm:tw-grid-cols-2":
                      parent?.length === 2,
                  })}
                >
                  {parent?.length > 0 ? (
                    parent?.map?.((v) => (
                      <StyledNewsEventCard
                        data={v}
                        key={v?.id}
                        single={parent?.length === 1}
                      />
                    ))
                  ) : (
                    <NoContent />
                  )}
                </div>
              ))
            ) : (
              <NoContent />
            )}
          </div>
          {pagination && (
            <NewPagination
              currentPage={currentPage}
              onPageChange={handlePageClick}
              totalPagesCount={totalPagesCount}
            />
          )}
        </>
      </CommonContainer>
    </>
  );
}

export default ResearchCenterListComponent;
